// Per-cohort breakdown (FM-6) — opened from the info button on each table row.
//
// Answers "why is this team this colour?": the four indicators with the RAG band
// each one landed in, the fortnight of daily signal underneath them, how much
// data the figures rest on, and the controls that apply at this tier.

import React from 'react';
import { Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { DerivedMetricKey, FatigueFilters, FatigueRiskTier } from '../data/types';
import { useCohortDaily, useFatigueSummary } from '../data/useFatigueData';
import { METRIC_DEFINITIONS, RAG_PROVISIONAL_NOTE, tierForMetric } from '../data/methodology';
import { LoadingBlock, NoteLine, RagRangeTable, StatRow, TierBadge, TIER_LABEL, tierTextClass } from './primitives';
import { CohortDailyChart } from './charts';
import { PanelSection, SidePanel } from './SidePanel';
import { cn } from '@/lib/utils';

/** What to do about a cohort at each tier. Operational, not clinical. */
const CONTROLS: Record<FatigueRiskTier, string[]> = {
  green: [
    'Keep the current rota and rest pattern under routine review.',
    'Watch the trend rather than the single reading.',
  ],
  amber: [
    'Review consecutive shifts and rest windows for this team.',
    'Check overtime allocation before adding further load.',
    'Offer sleep and recovery support to the team.',
  ],
  red: [
    'Review the rota with the team lead this week.',
    'Reduce or redistribute high-load tasks until readiness recovers.',
    'Protect rest periods between shifts and pause non-essential overtime.',
    'Check in with the team about workload and fatigue.',
  ],
};

export const CohortDetailPanel: React.FC<{
  cohortId: string | null;
  filters: FatigueFilters;
  onClose: () => void;
  /** FM-7 — hand over to individual-level detail. Absent when not permitted. */
  onDrillToIndividuals?: (cohortId: string) => void;
}> = ({ cohortId, filters, onClose, onDrillToIndividuals }) => {
  // The summary is already cached by the section behind this panel, so the
  // headline numbers here are the same objects the table row showed rather than
  // a second read that could disagree with it.
  const { data } = useFatigueSummary(filters);
  const summary = data?.cohorts.find((row) => row.cohort.id === cohortId);
  const { series, isFetching } = useCohortDaily(cohortId, filters);

  const metricRows: { key: DerivedMetricKey; value: number; display: string }[] = summary
    ? [
        { key: 'fed', value: summary.fedPer1000, display: `${summary.fedPer1000} per 1,000 workdays` },
        { key: 'lrm', value: summary.lrmPct, display: `${summary.lrmPct}% of shifts` },
        { key: 'cdi', value: summary.cdiScore, display: `${summary.cdiScore} / 100` },
        { key: 'pdc', value: summary.pdcPct, display: `${summary.pdcPct}% of cohort` },
      ]
    : [];

  const blank = Boolean(summary && (summary.suppressed || summary.noData));

  return (
    <SidePanel
      open={Boolean(cohortId)}
      onClose={onClose}
      width='lg'
      title={summary?.cohort.name ?? 'Cohort breakdown'}
      subtitle={
        summary
          ? `${summary.cohort.site} · ${summary.cohort.headcount} people · ${summary.cohort.shiftType} shift (inferred)`
          : undefined
      }
      footer={
        <>
          {onDrillToIndividuals && cohortId && !blank && (
            <Button variant='outline' size='sm' onClick={() => onDrillToIndividuals(cohortId)}>
              <Users className='mr-1.5 size-4' aria-hidden />
              View individuals
            </Button>
          )}
          <Button variant='outline' size='sm' onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {!summary ? (
        <LoadingBlock className='h-64' />
      ) : blank ? (
        <div className='flex flex-col'>
          <PanelSection title='Nothing to show yet'>
            <p className='text-sm text-muted-foreground'>
              {summary.noData
                ? 'Nobody in this team has usable wearable data in the current window.'
                : 'Too few people in this team have wearable data for their figures to be shown separately. Their data still counts towards the headline numbers.'}
            </p>
            <StatRow
              label='People wearing'
              value={`${summary.compliance.activeUsers} of ${summary.compliance.enrolledUsers}`}
            />
          </PanelSection>
        </div>
      ) : (
        <div className='flex flex-col'>
          <PanelSection title='Overall tier'>
            <div className='flex items-center gap-3'>
              <TierBadge tier={summary.riskTier} />
              <p className='text-sm text-muted-foreground'>
                A cohort takes the worst tier of its four indicators, so one red indicator makes the team red.
              </p>
            </div>
          </PanelSection>

          {metricRows.map((row) => {
            const def = METRIC_DEFINITIONS[row.key];
            const tier = tierForMetric(row.key, row.value);
            return (
              <PanelSection key={row.key} title={`${def.code} — ${def.name}`}>
                <div className='flex items-baseline justify-between gap-3'>
                  <span className={cn('text-lg font-semibold tabular-nums', tierTextClass[tier])}>{row.display}</span>
                  <span className={cn('text-sm font-medium', tierTextClass[tier])}>{TIER_LABEL[tier]}</span>
                </div>
                <p className='text-sm text-muted-foreground'>{def.cardNote}</p>
                <RagRangeTable metricKey={row.key} currentValue={row.value} />
              </PanelSection>
            );
          })}

          <PanelSection title='Last fortnight, day by day'>
            {isFetching && !series ? (
              <LoadingBlock className='h-48' />
            ) : series && series.some((point) => point.contributors > 0) ? (
              <>
                <CohortDailyChart points={series} />
                <NoteLine>
                  Cohort averages per day — sleep against readiness. Days where nobody in the team wore a device are
                  gaps in the lines, not zeros.
                </NoteLine>
              </>
            ) : (
              <p className='text-sm text-muted-foreground'>No daily signal for this team in the last fortnight.</p>
            )}
          </PanelSection>

          <PanelSection title='How reliable is this?'>
            <StatRow
              label='Data coverage'
              value={`${summary.compliance.coveragePct}%`}
              hint={`${summary.compliance.activeUsers} of ${summary.compliance.enrolledUsers} people wearing`}
            />
            <StatRow
              label='Data confidence'
              value={`${summary.compliance.confidenceScore}%`}
              hint={`${summary.compliance.missingDataDays} missing person-days in the window`}
            />
          </PanelSection>

          <PanelSection title='Controls to consider'>
            <ul className='flex flex-col gap-1.5'>
              {CONTROLS[summary.riskTier].map((control) => (
                <li key={control} className='flex gap-2 text-sm text-muted-foreground'>
                  <span aria-hidden>•</span>
                  <span>{control}</span>
                </li>
              ))}
            </ul>
            <NoteLine>{RAG_PROVISIONAL_NOTE}</NoteLine>
          </PanelSection>
        </div>
      )}
    </SidePanel>
  );
};
